import {
  Maybe as StructuredMaybe,
  nothing as structuredNothing,
} from '@execonline-inc/structured-maybe';
import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import ShareOptionsModal from '../../../LearningCollection/Share/ShareOptionsModal';
import LearningCollectionsStore from '../../../Store';
import CloneModal from './CloneModal';
import CloneStore from './Store';

interface Props {
  cloneStore: CloneStore;
  learningCollectionsStore: LearningCollectionsStore;
}

function Clone({ cloneStore, learningCollectionsStore }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isInviting, setIsInviting] = React.useState(false);
  const [publishedAt, setPublishedAt] = React.useState<StructuredMaybe<Date>>(structuredNothing());

  // Refresh the list as soon as the clone lands so the new draft is already
  // in place behind the modal, not only after it is dismissed.
  const stateKind = cloneStore.state.kind;
  React.useEffect(() => {
    if (stateKind === 'cloned') {
      queryClient.invalidateQueries({ queryKey: ['learning-collections-index'] });
    }
  }, [stateKind, queryClient]);

  const handleClose = () => {
    if (cloneStore.state.kind === 'cloned') {
      learningCollectionsStore.collectionCloned(cloneStore.state.newCollectionId);
    }
    setIsInviting(false);
    setPublishedAt(structuredNothing());
    cloneStore.reset();
  };

  switch (cloneStore.state.kind) {
    case 'idle':
      return <></>;
    case 'cloning':
      return <CloneModal kind="cloning" collectionName={cloneStore.state.collectionName} />;
    case 'clone-failed':
      return <CloneModal kind="clone-failed" onClose={handleClose} />;
    case 'cloned': {
      const { newCollectionId, collectionName, coursesNeedReview } = cloneStore.state;
      return isInviting ? (
        <ShareOptionsModal
          close={handleClose}
          collectionId={newCollectionId}
          publishedAt={publishedAt}
          onPublishSuccess={setPublishedAt}
        />
      ) : (
        <CloneModal
          kind="cloned"
          collectionName={collectionName}
          coursesNeedReview={coursesNeedReview}
          onClose={handleClose}
          onEdit={() => navigate(`/learning_collections/builder_v2/${newCollectionId}`)}
          onPublishAndInvite={() => setIsInviting(true)}
        />
      );
    }
  }
}

export default observer(Clone);
